import centersSeed from "@/data/centers.generated.json";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";
import { API, whatsappLink, formatMobile, validateSpanishPhone } from "@/lib/faro";
import { trackClick } from "@/lib/analytics";

const PERMITS = ["B", "AM", "AML", "A1", "A2", "A", "B96", "B+E", "C", "C+E", "D", "CAP", "Recuperación de puntos"];
const INPUT_CLS = "mt-1 w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-3 outline-none focus:border-[var(--faro-blue)] focus:ring-2 focus:ring-[rgba(30,42,143,0.1)] transition";
const LABEL_CLS = "text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className={LABEL_CLS}>{label}</span>
    {children}
  </label>
);

const CenterQuickLinks = ({ centers }) => (
  <div className="mt-7 space-y-3">
    {centers.map((c) => (
      <a
        key={c.id}
        href={whatsappLink(c.whatsapp, `Hola, quiero información de la sede de ${c.name}`)}
        target="_blank"
        rel="noreferrer"
        data-testid={`contact-quick-wa-${c.id}`}
        className="flex items-center justify-between p-3 rounded-xl border border-[rgba(30,42,143,0.12)] bg-white hover:border-[var(--faro-blue)] transition-colors"
      >
        <div>
          <div className="font-bold text-[var(--faro-text)]">{c.name}</div>
          <div className="text-xs text-[var(--faro-text-soft)]">Fijo: {c.phone_landline}</div>
          <div className="text-xs font-semibold text-[#25d366] flex items-center gap-1 mt-0.5">
            <MessageCircle size={12} /> Móvil: {formatMobile(c.whatsapp)}
          </div>
        </div>
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#25d366] shrink-0">
          <MessageCircle size={16} /> WhatsApp
        </span>
      </a>
    ))}
  </div>
);

const ContactIntro = ({ centers }) => (
  <div className="lg:col-span-5">
    <span className="eyebrow">Matrícula & contacto</span>
    <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
      Hablamos en menos de 24h.
    </h2>
    <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
      Cuéntanos qué carnet te interesa y por qué sede prefieres empezar. Tu solicitud llega directa por WhatsApp a la sede más cercana.
    </p>
    <CenterQuickLinks centers={centers} />
  </div>
);

function validateContact(form, consent) {
  if (!form.name || !form.phone || !form.center) return "Rellena nombre, teléfono y centro.";
  const phoneCheck = validateSpanishPhone(form.phone);
  if (!phoneCheck.ok) return phoneCheck.error;
  if (!consent) return "Debes aceptar la política de privacidad para enviar el formulario.";
  return null;
}

function buildContactWaUrl(form, centers) {
  const center = centers.find((c) => c.id === form.center);
  if (!center) return null;
  const msg = `Hola, soy ${form.name}. Me interesa el permiso ${form.permit_interest || "(consultar)"}. Mi teléfono: ${form.phone}. ${form.message || ""}`;
  return whatsappLink(center.whatsapp, msg);
}

const ContactFields = ({ form, onChange, centers, phoneError }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Nombre*">
        <input name="name" value={form.name} onChange={onChange} data-testid="contact-name" className={INPUT_CLS} placeholder="Tu nombre" />
      </Field>
      <Field label="Teléfono*">
        <input
          name="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          maxLength={20}
          value={form.phone}
          onChange={onChange}
          data-testid="contact-phone"
          aria-invalid={phoneError ? "true" : "false"}
          aria-describedby={phoneError ? "contact-phone-err" : undefined}
          className={`${INPUT_CLS} ${phoneError ? "!border-[var(--faro-red)] focus:!border-[var(--faro-red)] focus:!ring-[rgba(230,57,70,0.15)]" : ""}`}
          placeholder="6XX XXX XXX"
        />
        {phoneError && (
          <p id="contact-phone-err" data-testid="contact-phone-error" className="mt-1 text-xs font-semibold text-[var(--faro-red)]">
            {phoneError}
          </p>
        )}
        <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-[var(--faro-text-soft)] cursor-pointer select-none">
          <input
            type="checkbox"
            name="no_whatsapp"
            checked={!!form.no_whatsapp}
            onChange={(e) => onChange({ target: { name: "no_whatsapp", value: e.target.checked } })}
            data-testid="contact-no-whatsapp"
            className="accent-[var(--faro-red)]"
          />
          <span className="uppercase tracking-wider">No tengo WhatsApp</span>
          <span className="normal-case font-normal text-[var(--faro-text-soft)]">— te llamaremos o enviaremos SMS.</span>
        </label>
      </Field>
    </div>

    <Field label="Email">
      <input type="email" name="email" value={form.email} onChange={onChange} data-testid="contact-email" className={INPUT_CLS} placeholder="tunombre@email.com" />
    </Field>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Centro más cercano*">
        <select name="center" value={form.center} onChange={onChange} data-testid="contact-center" className={`${INPUT_CLS} bg-white`}>
          {centers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </Field>
      <Field label="¿Qué carnet te interesa?">
        <select name="permit_interest" value={form.permit_interest} onChange={onChange} data-testid="contact-permit" className={`${INPUT_CLS} bg-white`}>
          <option value="">Selecciona uno...</option>
          {PERMITS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </Field>
    </div>

    <Field label="¿Algo más?">
      <textarea name="message" rows={3} value={form.message} onChange={onChange} data-testid="contact-message" className={INPUT_CLS} placeholder="Horarios preferentes, dudas, etc." />
    </Field>
  </>
);

const ConsentCheckbox = ({ consent, setConsent }) => (
  <label className="flex items-start gap-2 text-xs text-[var(--faro-text-soft)] cursor-pointer">
    <input
      type="checkbox"
      checked={consent}
      onChange={(e) => setConsent(e.target.checked)}
      data-testid="contact-consent"
      className="mt-0.5 accent-[var(--faro-red)]"
    />
    <span>
      He leído y acepto la{" "}
      <a href="/politica-privacidad" target="_blank" rel="noreferrer" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">política de privacidad</a>.
      Mis datos serán tratados por Automovilística Faro S.L. para atender mi solicitud (también por WhatsApp). *
    </span>
  </label>
);

export default function ContactForm() {
  const [centers, setCenters] = useState(Array.isArray(centersSeed) ? centersSeed : []);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", no_whatsapp: false, email: "", center: "", permit_interest: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/centers`).then((r) => {
      setCenters(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, center: r.data[0].id }));
    }).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Error inline del teléfono: solo mostrar cuando el usuario ya ha empezado a escribir
  const phoneError = form.phone.trim() ? (validateSpanishPhone(form.phone).error || "") : "";

  const submit = async (e) => {
    e.preventDefault();
    const error = validateContact(form, consent);
    if (error) {
      toast.error(error);
      return;
    }
    setSending(true);
    const waUrl = buildContactWaUrl(form, centers);
    try {
      await axios.post(`${API}/contact`, { ...form, source: "web" });
      trackClick("form_submit_contact", { center: form.center, extra: { permit_interest: form.permit_interest || null } });
      toast.success("Solicitud enviada. Redirigiendo a WhatsApp...");
    } catch {
      toast.error("No se pudo guardar tu solicitud, pero te llevamos a WhatsApp para que contactes directamente.");
    } finally {
      setSending(false);
    }
    if (waUrl) window.location.href = waUrl;
  };

  return (
    <section id="contacto" className="section-pad bg-[var(--faro-bg)]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <ContactIntro centers={centers} />

        <form onSubmit={submit} className="lg:col-span-7 rounded-3xl bg-white border border-[rgba(30,42,143,0.12)] p-6 sm:p-8 shadow-xl shadow-[rgba(30,42,143,0.06)] space-y-4" data-testid="contact-form">
          <ContactFields form={form} onChange={onChange} centers={centers} phoneError={phoneError} />

          <ConsentCheckbox consent={consent} setConsent={setConsent} />

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[var(--faro-text-soft)]">
              Al enviar, te redirigimos al WhatsApp de tu sede para acabar la conversación al instante.
            </p>
            <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-pill btn-primary disabled:opacity-60">
              {sending ? "Enviando..." : "Enviar y abrir WhatsApp"} <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
